import { useState, useCallback, useRef, useEffect, type FC } from 'react';
import { useRouter } from 'src/hooks/use-router';
import { useLocation } from 'react-router-dom';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import XCloseIcon from '@untitled-ui/icons-react/build/esm/XClose';
import Mail01Icon from '@untitled-ui/icons-react/build/esm/Mail01';
import CheckSquareIcon from '@untitled-ui/icons-react/build/esm/CheckSquare';
import HomeLineIcon from '@untitled-ui/icons-react/build/esm/HomeLine';
import Users03Icon from '@untitled-ui/icons-react/build/esm/Users03';
import BarChartSquare01Icon from '@untitled-ui/icons-react/build/esm/BarChartSquare01';
import Announcement01Icon from '@untitled-ui/icons-react/build/esm/Announcement01';
import LifeBuoy01Icon from '@untitled-ui/icons-react/build/esm/LifeBuoy01';
import Building02Icon from '@untitled-ui/icons-react/build/esm/Building02';
import ZapIcon from '@untitled-ui/icons-react/build/esm/Zap';
import Settings01Icon from '@untitled-ui/icons-react/build/esm/Settings01';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/Calendar';
import File06Icon from '@untitled-ui/icons-react/build/esm/File06';
import RouteIcon from '@untitled-ui/icons-react/build/esm/Route';
import ClipboardCheckIcon from '@untitled-ui/icons-react/build/esm/ClipboardCheck';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import ChatIcon from '@mui/icons-material/Chat';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { leadsApi } from 'src/api/leads';
import type { LeadV5 } from 'src/api/types';
import { getInitials } from 'src/utils/get-initials';
import { useCompanySettings } from 'src/hooks/use-company-settings';
import { useAuth } from 'src/hooks/use-auth';

export const SIDE_NAV_V2_WIDTH = 220;

interface NavItem {
  label: string;
  path: string;
  isSearch?: boolean;
  exact?: boolean;
  icon?: React.ElementType;
}

interface NavSection {
  label?: string;
  items: NavItem[];
  collapsible?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { label: 'Search', path: '', isSearch: true, icon: SearchMdIcon },
      { label: 'Inbox', path: '/dashboard/inbox', icon: Mail01Icon },
      { label: 'Tasks', path: '/dashboard/tasks', icon: CheckSquareIcon },
      { label: 'Calendar', path: '/dashboard/calendar', icon: CalendarIcon },
    ],
  },
  {
    items: [
      { label: 'Dashboard', path: '/dashboard', exact: true, icon: HomeLineIcon },
      { label: 'Clients', path: '/dashboard/customers', icon: Users03Icon },
      { label: 'Sales Pipeline', path: '/dashboard/sales-pipeline', icon: BarChartSquare01Icon },
      { label: 'Marketing', path: '/dashboard/marketing', icon: Announcement01Icon },
      { label: 'Support', path: '/dashboard/support-hub', icon: LifeBuoy01Icon },
    ],
  },
  {
    items: [
      { label: 'Company', path: '/dashboard/company', icon: Building02Icon },
      { label: 'Integrations', path: '/dashboard/tools', icon: ZapIcon },
      { label: 'Workflows', path: '/dashboard/workflows', icon: RouteIcon },
      { label: 'Settings', path: '/dashboard/settings', icon: Settings01Icon },
      { label: 'Logs', path: '/dashboard/logs', icon: ClipboardCheckIcon },
    ],
  },
  // AI & Agents rendered as a special card section below
];

interface AgentNavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

const ANDREA_NAV_ITEMS: AgentNavItem[] = [
  { label: 'Chat', path: '/dashboard/andrea/chat', icon: ChatIcon },
  { label: 'Call Log', path: '/dashboard/andrea/call-log', icon: PhoneIcon },
  { label: 'Message Log', path: '/dashboard/andrea/message-log', icon: SmsIcon },
  { label: 'Config', path: '/dashboard/andrea/config', icon: SettingsIcon },
];

interface SideNavV2Props {
  open?: boolean;
  onClose?: () => void;
}

export const SideNavV2: FC<SideNavV2Props> = ({ open, onClose }) => {
  const router = useRouter();
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { settings: company } = useCompanySettings();
  const { user } = useAuth();
  const isSalesman = user?.role === 'salesman';

  // Upgrade card dismissed state
  const [upgradeDismissed, setUpgradeDismissed] = useState(() => localStorage.getItem('upgrade-card-dismissed') === 'true');
  const handleDismissUpgrade = useCallback(() => {
    setUpgradeDismissed(true);
    localStorage.setItem('upgrade-card-dismissed', 'true');
  }, []);

  // Collapsible section state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = useCallback((label: string) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);

  // Search state
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<LeadV5[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showResults = isFocused && (searchValue.length > 0 || searchResults.length > 0);

  const searchCustomers = useCallback(async (query: string) => {
    if (!query.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const response = await leadsApi.getAll({ search: query, pageSize: 50 });
      const allLeads = response.data.map((lead: any) => {
        if (lead.attributes) return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
        return lead;
      }) as LeadV5[];
      const queryLower = query.toLowerCase().trim();
      const filteredLeads = allLeads.filter((lead) => {
        const firstName = (lead.firstName || '').toLowerCase();
        const lastName = (lead.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;
        return firstName.startsWith(queryLower) || lastName.startsWith(queryLower) || fullName.startsWith(queryLower);
      });
      setSearchResults(filteredLeads.slice(0, 10));
    } catch { setSearchResults([]); }
    finally { setIsSearching(false); }
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setSearchResults([]); setIsSearching(false); return; }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => searchCustomers(value), 200);
  }, [searchCustomers]);

  const handleSelectCustomer = useCallback((customer: LeadV5) => {
    router.push(`/dashboard/customers/${customer.documentId || String(customer.id)}`);
    setSearchValue('');
    setSearchResults([]);
    setIsFocused(false);
    searchInputRef.current?.blur();
  }, [router]);

  const handleCloseResults = useCallback(() => setIsFocused(false), []);
  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setSearchResults([]);
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);



  const isActive = (item: NavItem) => {
    if (item.isSearch) return false;
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const isMobileDrawer = typeof onClose === 'function';

  const navContent = (
    <Box
      component="nav"
      sx={{
        ...(isMobileDrawer ? {
          width: SIDE_NAV_V2_WIDTH,
          height: '100%',
        } : {
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: SIDE_NAV_V2_WIDTH,
          zIndex: 3,
        }),
        borderRight: isMobileDrawer ? 'none' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
        bgcolor: isDarkMode ? '#111113' : '#F2F8FC',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Logo Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          component={RouterLink}
          href={paths.index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
          }}
        >
          <Box
            sx={{
              height: 28,
              width: 28,
              borderRadius: isDarkMode ? '8px' : '50%',
              overflow: 'hidden',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'transparent' : '#000',
            }}
          >
            <Box
              component="img"
              src="/assets/logo-main.png"
              alt="Mystic Media"
              sx={{ height: 22, width: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '0.88rem',
              fontWeight: 700,
              color: isDarkMode ? '#fff' : '#0f172a',
              letterSpacing: '-0.02em',
            }}
          >
            Mystic Media
          </Typography>
        </Box>
        {/* Top-right action icons (like reference) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={() => { searchInputRef.current?.focus(); setIsFocused(true); }}
            sx={{
              p: 0.5,
              color: isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
              '&:hover': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <SvgIcon sx={{ fontSize: 15 }}><SearchMdIcon /></SvgIcon>
          </IconButton>
        </Box>
      </Box>

      {/* Nav Items */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, pt: 0.5 }}>
        {/* Colored dots for document folder items */}
        {(isSalesman
          ? ([{
              items: [
                { label: 'Calendar', path: '/dashboard/calendar', icon: CalendarIcon },
                { label: 'Leads', path: '/dashboard/customers', icon: Users03Icon },
                { label: 'Inbox', path: '/dashboard/inbox', icon: Mail01Icon },
                { label: 'Pipeline', path: '/dashboard/sales-pipeline', icon: BarChartSquare01Icon },
                { label: 'Logs', path: '/dashboard/logs', icon: ClipboardCheckIcon },
              ],
            }] as NavSection[])
          : NAV_SECTIONS
        ).map((section, sectionIndex) => {
          const docColors = ['#10B981', '#F59E0B', '#8B5CF6'];
          return (
          <Box key={sectionIndex}>
            {sectionIndex > 0 && (
              <Box
                sx={{
                  height: '1px',
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  mx: 1,
                  my: 1,
                }}
              />
            )}
            {section.label && (
              <Box
                onClick={section.collapsible ? () => toggleSection(section.label!) : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  pt: 0.5,
                  pb: 0.5,
                  cursor: section.collapsible ? 'pointer' : 'default',
                  borderRadius: '6px',
                  mx: 0.5,
                  '&:hover': section.collapsible ? {
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  } : {},
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)',
                    userSelect: 'none',
                  }}
                >
                  {section.label}
                </Typography>
                {section.collapsible && (
                  <SvgIcon sx={{
                    fontSize: 12,
                    color: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    transition: 'transform 0.2s ease',
                    transform: collapsedSections[section.label!] ? 'rotate(0deg)' : 'rotate(90deg)',
                  }}>
                    <ChevronRightIcon />
                  </SvgIcon>
                )}
              </Box>
            )}
            <Collapse in={!section.collapsible || !collapsedSections[section.label || '']} timeout={200}>
            {section.items.map((item) => {
              // Search item
              if (item.isSearch) {
                return (
                  <Box key="search" ref={searchBoxRef} sx={{ mx: 0.5, mb: 0.2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.7,
                        borderRadius: '7px',
                        bgcolor: 'transparent',
                      }}
                    >
                      <SvgIcon sx={{ fontSize: 16, color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', flexShrink: 0 }}>
                        <SearchMdIcon />
                      </SvgIcon>
                      <Box
                        component="input"
                        ref={searchInputRef}
                        value={searchValue}
                        onChange={handleSearchChange}
                        onFocus={() => setIsFocused(true)}
                        placeholder="Search"
                        sx={{
                          border: 'none',
                          outline: 'none',
                          background: 'none',
                          width: '100%',
                          fontSize: '0.8rem',
                          fontWeight: 420,
                          fontFamily: 'inherit',
                          color: isDarkMode ? '#e0e0e0' : '#1a1a1a',
                          p: 0,
                          '&::placeholder': { color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)' },
                        }}
                      />
                      {searchValue && (
                        isSearching ? (
                          <CircularProgress size={12} sx={{ color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', flexShrink: 0 }} />
                        ) : (
                          <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.2, flexShrink: 0 }}>
                            <SvgIcon sx={{ fontSize: 11, color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)' }}>
                              <XCloseIcon />
                            </SvgIcon>
                          </IconButton>
                        )
                      )}
                    </Box>

                    {/* Search Results Dropdown */}
                    <Popper
                      open={showResults}
                      anchorEl={searchBoxRef.current}
                      placement="bottom-start"
                      style={{ zIndex: 1400, width: 280 }}
                      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                    >
                      <ClickAwayListener onClickAway={handleCloseResults}>
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: '10px',
                            overflow: 'hidden',
                            bgcolor: isDarkMode ? '#141414' : '#F8F7F4',
                            boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.6)' : '0 8px 32px rgba(0,0,0,0.12)',
                            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                          }}
                        >
                          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                            {isSearching && searchResults.length === 0 ? (
                              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={16} sx={{ color: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }} />
                              </Box>
                            ) : searchValue && searchResults.length === 0 && !isSearching ? (
                              <Box sx={{ py: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>No results found</Typography>
                              </Box>
                            ) : (
                              <List disablePadding>
                                {searchResults.map((customer) => {
                                  const name = `${customer.firstName} ${customer.lastName}`;
                                  const subtitle = customer.email || customer.phone || [customer.city, customer.state].filter(Boolean).join(', ');
                                  return (
                                    <ListItemButton
                                      key={customer.documentId || customer.id}
                                      onClick={() => handleSelectCustomer(customer)}
                                      sx={{ py: 0.6, px: 1.5, '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' } }}
                                    >
                                      <ListItemAvatar sx={{ minWidth: 34 }}>
                                        <Avatar sx={{ width: 26, height: 26, fontSize: '0.65rem', fontWeight: 600, bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                          {getInitials(name)}
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText
                                        primary={<Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: isDarkMode ? '#e0e0e0' : '#1a1a1a' }} noWrap>{name}</Typography>}
                                        secondary={subtitle && <Typography sx={{ fontSize: '0.68rem', color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)' }} noWrap>{subtitle}</Typography>}
                                      />
                                    </ListItemButton>
                                  );
                                })}
                              </List>
                            )}
                          </Box>
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </Box>
                );
              }

              // Regular nav item
              const active = isActive(item);
              const ItemIcon = item.icon;
              return (
                <Box
                  key={item.path}
                  component={RouterLink}
                  href={item.path}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    px: 1.5,
                    py: 0.65,
                    mx: 0.5,
                    mb: 0.15,
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: active ? 550 : 420,
                    color: active
                      ? (isDarkMode ? '#fff' : '#0f172a')
                      : (isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)'),
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease',
                    bgcolor: active
                      ? (isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)')
                      : 'transparent',
                    '&:hover': {
                      bgcolor: active
                        ? (isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)')
                        : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                      color: active
                        ? (isDarkMode ? '#fff' : '#0f172a')
                        : (isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.65)'),
                    },
                  }}
                >
                  {section.label ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: docColors[section.items.indexOf(item) % docColors.length],
                        opacity: active ? 1 : 0.5,
                        flexShrink: 0,
                      }}
                    />
                  ) : ItemIcon ? (
                    <SvgIcon sx={{ fontSize: 16, opacity: active ? 0.9 : 0.4 }}>
                      <ItemIcon />
                    </SvgIcon>
                  ) : null}
                  {item.label}
                </Box>
              );
            })}
            </Collapse>
          </Box>
        )})}

      </Box>



      {/* Upgrade Card — hidden for salesman */}
      {!upgradeDismissed && !isSalesman && (
        <Box sx={{ px: 1.5, pb: 2, flexShrink: 0 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              position: 'relative',
            }}
          >
            {/* Close button */}
            <IconButton
              size="small"
              onClick={handleDismissUpgrade}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                p: 0.3,
                color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
                '&:hover': { color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)', bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <SvgIcon sx={{ fontSize: 13 }}><XCloseIcon /></SvgIcon>
            </IconButton>
            {/* Avatar icon */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <SvgIcon sx={{ fontSize: 16, color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }}>
                <ZapIcon />
              </SvgIcon>
            </Box>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: isDarkMode ? '#fff' : '#0f172a',
                mb: 0.25,
              }}
            >
              Upgrade to Premium!
            </Typography>
            <Typography
              sx={{
                fontSize: '0.68rem',
                color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                lineHeight: 1.45,
                mb: 1.25,
              }}
            >
              Send unlimited contracts.{'\n'}Customize for your brand.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="small"
              sx={{
                bgcolor: isDarkMode ? '#fff' : '#0f172a',
                color: isDarkMode ? '#000' : '#fff',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.76rem',
                py: 0.7,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.9)' : '#1e293b',
                  boxShadow: 'none',
                },
              }}
            >
              Upgrade Now
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  if (isMobileDrawer) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        variant="temporary"
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? '#111113' : '#F2F8FC',
            width: SIDE_NAV_V2_WIDTH,
            borderRight: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDarkMode
              ? '4px 0 24px rgba(0,0,0,0.5)'
              : '4px 0 24px rgba(0,0,0,0.08)',
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {navContent}
      </Drawer>
    );
  }

  return navContent;
};
